import React, {ComponentType, useEffect, useState} from "react";
import StandbyTableSeater from "external-components/standby-list-table-seater";
import {classPrefix, IContainerProps, IHandlers, IProps, IServices} from 'external-components/standby-list-table-seater/types'
import NetworkConnectionService from 'external-services/network-connection-service/index';

/**
 * Gets the interface types as array of strings so we don't need hand code them each time (and reduces chance for mistakes).
 */
import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import ReactUtilsService from "app/services/reactUtils.service";
import {StandbyListService as LegacyStandbyListService} from "app/services/standbyList/StandbyList.service";
import TableSelectionService from "external-services/table-selection-service/index";
import {AdminServiceLog} from "app/app.types";
import {IConfirmStandbyReponse} from "app/actions/editBooking.actions";
import TableJoinService, {JOIN_PREFIX} from "app/services/tableJoin/tableJoin.service";
import {STATUS_CHANGE} from "app/actions/networkConnection.actions";
import {uniqueCssClassPrefix} from "app/diary.module.helpers";
import AdminService from "external-services/diary-admin-service/admin.service";
import {ITableItem, servicePaymentType, StatusStandbyPending} from 'shared-types/index';
import {BookingStatusNames} from "app/bookingStatusTypes";

const NS = 'StandbyTableSeaterContainer';


const propsKeys: string[] = keys<IProps>();
const containerPropsKeys: string[] = keys<IContainerProps>();
const handlersKeys: string[] = keys<IHandlers>();
const servicesKeys: string[] = keys<IServices>();


const StandbyTableSeaterWrap = function (all: IProps & IContainerProps & IHandlers & IServices) {

  // gets props and handlers logic from TableSeater to inject
  const tableSeaterProps = TableSelectionService.getTableSeaterProps(all);
  const hasPreselectedTableIds: boolean = !!(tableSeaterProps.props.preselectedTableIds && tableSeaterProps.props.preselectedTableIds.length);

  /**
   * Sorts 'all' into IServices, IProps and IContainerProps
   */
  const {EditBookingActions, $rootScope}: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices;


  const props: IProps = {
    ...tableSeaterProps.props, // extends TableSeater props
    ...(ReactUtilsService.getObjectByKeys(all, propsKeys) as IProps)
  };
  const {
    booking, selectedTime, times, isAllowedToSend, isFlexibleTime, hasPriceChanges,
    onStandbyUpdate, onTimeChanged, handleSeatNow, handleCancelBooking
  }: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>(null);
  const [isSelectionValid, setIsSelectionValid] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(
    AdminService.getVenue().isOnlineCustomersEnabled && !NetworkConnectionService.isOnline()
  );

  useEffect(() => {
    const unlisten = $rootScope.$on(STATUS_CHANGE, (evt) => {
      setIsOffline(!NetworkConnectionService.isOnline());
    });
    return () => {
      unlisten();
    };
  }, [isOffline]);

  const priceExistsAndChanged: boolean = hasPriceChanges && booking.paymentDetails.paymentType !== servicePaymentType.noPayment;
  const hasSelectedTablesAndWasInvalid = hasPreselectedTableIds && !isSelectionValid;
  const noSelectedTablesAndWasValid = !hasPreselectedTableIds && isSelectionValid;

  // there is a delay on `tableSeaterProps.props.preselectedTableIds` being set, so can't set it as `isSelectionValid` default value
  if (hasSelectedTablesAndWasInvalid) {
    setIsSelectionValid(true);
  } else if (noSelectedTablesAndWasValid) { // if preselectedTableIds is cleared externally
    setIsSelectionValid(false);
  }

  props.errorMsg = errorMsg;
  props.isLoading = isLoading;
  props.selectedTime = selectedTime.time;
  props.isSelectionValid = isSelectionValid;
  props.isSelectionValid = isSelectionValid;
  props.times = times;
  props.isFlexibleTime = isFlexibleTime;
  props.isOffine = isOffline;
  props.customerPhone = booking.phone;
  props.priceExistsAndChanged = priceExistsAndChanged;

  /**
   * Handlers used to modify app state (via a service) from within the module's container.
   * Similar to redux mapDispatchToProps.
   */
  const handlers: IHandlers = {
    ...tableSeaterProps.handlers, // extends TableSeater handlers

    handleSelectedChanged: (selectedTableIds: string[], tables: ITableItem[], isManualJoin: boolean) => {
      setIsSelectionValid(!!selectedTableIds.length);

      // extends TableSeater handlers
      tableSeaterProps.handlers.handleSelectedChanged(selectedTableIds, tables, isManualJoin);
    },

    handleSendClick: () => {
      if (!isAllowedToSend) {
        onStandbyUpdate(false, null, true);
        return;
      }
      const passesValidation = onStandbyUpdate(true, null); // starts loading status and updates booking object
      if (!passesValidation) {
        return;
      }

      setErrorMsg(null);
      setIsLoading(true);

      // using the first selectedTableId because if using the layout-based table join, which is prefixed with 'join_', there can only be one selected.
      const firstSelectedTableId = props.preselectedTableIds[0];
      if (firstSelectedTableId && firstSelectedTableId.indexOf(JOIN_PREFIX) === 0) {
        const isManualJoin = false;
        booking.tables = TableJoinService.convertToManualJoin(props.preselectedTableIds, booking.tables, isManualJoin);
      }

        // adding server logging to capture evidence when tables = null - NBI-3868
        // the condition [tables: null] triggers an exception on the backend side that affects the Widget loading
        // However, bookings with BookingType.StandbyList or BookingType.WaitList are able to save with empty tables (BUT NOT NULL).
        //
        // tables: []    => EXCEPTION FREE
        // tables: null  => TRIGGERS EXCEPTION ON API
        if (!booking.tables) {
        console.log("WARNING - DETECTED NULL TABLES ON BOOKING - UPDATE SBL");
        const notes = `Booking Data: {"_id": "${booking._id}",
                      "_rev": "${booking._rev}", "bookingType": "${booking.bookingType}", "tables": "${booking.tables}"}`;
        AdminService.serverLoggingDbEvents(AdminService.getVenue().id.toString(), "Updated Booking with NULL tables. - SBL", AdminServiceLog.ERRORLOG, notes);
      }

      if (isOffline) {
        setIsLoading(false);
        booking.status = StatusStandbyPending;
        onStandbyUpdate(false, booking, false, BookingStatusNames.StandbyPending);
        return;
      }
      EditBookingActions.confirmStandby(booking)
        .subscribe(({status, booking}: IConfirmStandbyReponse) => {
          setIsLoading(false);

          switch (status) {
            case 200:
              onStandbyUpdate(false, booking);
              break;

            case 408:
              setErrorMsg('Sorry, server took too long to respond (timeout). Try closing and reopening the booking.');
              onStandbyUpdate(false, null);
              break;

            default:
              console.warn(NS, 'confirmStandby failed', status);
              setErrorMsg(LegacyStandbyListService.getGenericErrorResponseMessaging(status));
              onStandbyUpdate(false, null);
          }
        }, (err: string) => {
          console.warn(NS, 'confirmStandby failed', err);
          setIsLoading(false);
          onStandbyUpdate(false, null);
          setErrorMsg('Sorry, there seems to have been a problem with your internet connection.');
        });
    },

    handleTimeChanged: onTimeChanged,
    handleSeatNow,
    handleCancelBooking
  }

  /**
   * Needed to get unique class names each time because there is a risk of class name clashes between different
   * instances of the same component. They use a counter to generate uniqueness, but this starts at 0 for each
   * component and therefore needs some other unique prefix, other than just the static 'classPrefix'.
   * Generating it on each render is a bit overkill, but there is no reliable way to get an id for each instance.
   */
  const uniqueClassPrefix = `${classPrefix}-${uniqueCssClassPrefix()}`;
  props.classPrefix = uniqueClassPrefix;

  return ReactUtilsService.containerWrap(
    <StandbyTableSeater {...props} {...handlers} />, uniqueClassPrefix
  );
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2AStandbyTableSeater: [ComponentType<unknown>, never[]?, string[]?] = [
  hot(module)(StandbyTableSeaterWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[],
  servicesKeys
];
